/**
 * <TickCell> — 13-ui-screens.md §3.7, §7.7.
 *
 * NOT antd Checkbox. The prototype's tick is a 19 × 19 box whose whole 28 px
 * cell is the hit area. antd builds its checkbox from inner spans with scale
 * transforms, and restyling it means reaching into antd's private inner
 * classes, which is forbidden (README §4 conflict #20: no antd internal
 * selectors at all, absolute and lintable).
 *
 * The 15 × 15 ring radio this file used to export for the crew grid's leader
 * column (selected state `border: 5px solid`) was removed at ADR-0013
 * (2026-09-06, zero call sites): the leader is now an effective-dated window
 * on the team, set on the Teams page, not a per-line radio in this grid.
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';

export interface TickCellProps {
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
  /** Share text under the box, e.g. `RM 50.40`. Grows the row deliberately. */
  subLabel?: ReactNode;
  /** Accessible name — the grid gives every cell a worker × job meaning. */
  label: string;
  /** `.4` opacity + not-allowed when the worker carries an exception. */
  excepted?: boolean;
}

const BOX_BASE: CSSProperties = {
  width: 19,
  height: 19,
  borderRadius: 3,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  lineHeight: 1,
  fontWeight: 700,
};

export function TickCell({
  checked,
  disabled = false,
  onToggle,
  subLabel,
  label,
  excepted = false,
}: TickCellProps) {
  const interactive = !disabled && !excepted;

  const box: CSSProperties = checked
    ? { ...BOX_BASE, border: '1px solid var(--brand-primary)', background: 'var(--brand-primary)', color: 'var(--white)' }
    : disabled || excepted
      ? { ...BOX_BASE, border: '1px solid var(--border-subtle)', background: 'var(--neutral-100)' }
      : { ...BOX_BASE, border: '1px solid var(--border-default)', background: 'var(--white)' };

  function activate(): void {
    if (interactive) onToggle();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      activate();
    }
  }

  return (
    <div
      className={interactive ? 'cw-cell' : undefined}
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      aria-disabled={!interactive}
      tabIndex={interactive ? 0 : -1}
      onClick={activate}
      onKeyDown={onKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        width: '100%',
        minHeight: 30,
        borderRadius: 3,
        cursor: interactive ? 'pointer' : 'not-allowed',
        opacity: excepted ? 0.4 : 1,
      }}
    >
      <span style={box}>{checked ? '✓' : ''}</span>
      {subLabel ? <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{subLabel}</span> : null}
    </div>
  );
}
